import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { getBuiltInGuideTopic } from '../../content/gameGuideBuiltIn';
import { getGamePageContent } from '../../content/gameInfo';
import NewsContent from '../../components/NewsContent';
import { guideService } from '../../services/api';
import type { GuideArticle } from '../../types';
import { guideText } from '../../utils/guideLocale';
import { renderGuideContent } from '../../utils/renderGuideContent';

const GameGuideArticle: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { locale } = useLanguage();
    const [article, setArticle] = useState<GuideArticle | null>(null);
    const [useBuiltIn, setUseBuiltIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const builtIn = slug ? getBuiltInGuideTopic(slug, locale) : null;
    const pageContent = getGamePageContent(locale);

    useEffect(() => {
        if (!slug) return;
        const load = async () => {
            setLoading(true);
            setError(null);
            setUseBuiltIn(false);
            try {
                const data = await guideService.getArticle(slug);
                setArticle(data);
            } catch {
                const fallback = getBuiltInGuideTopic(slug, locale);
                if (fallback) {
                    setArticle(null);
                    setUseBuiltIn(true);
                } else {
                    setArticle(null);
                    setError(
                        locale === 'pt-BR' ? 'Tutorial não encontrado.' : 'Tutorial not found.'
                    );
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [slug, locale]);

    const backLabel = locale === 'pt-BR' ? 'Voltar ao guia' : 'Back to guide';
    const resolvedBuiltIn = useBuiltIn && builtIn ? builtIn : null;

    if (loading) {
        return (
            <div className="game-main-inner">
                <p className="game-loading">{locale === 'pt-BR' ? 'Carregando...' : 'Loading...'}</p>
            </div>
        );
    }

    if (error && !resolvedBuiltIn && !article) {
        return (
            <div className="game-main-inner">
                <p className="game-error">{error}</p>
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/game')}>
                    {backLabel}
                </button>
            </div>
        );
    }

    const title = resolvedBuiltIn
        ? resolvedBuiltIn.title
        : guideText(locale, article!.titleEn, article!.titlePtBr);

    const summary = resolvedBuiltIn
        ? resolvedBuiltIn.summary
        : guideText(locale, article!.summaryEn, article!.summaryPtBr);

    const body = resolvedBuiltIn
        ? resolvedBuiltIn.content
        : guideText(locale, article!.contentEn, article!.contentPtBr);

    const isFaq = (resolvedBuiltIn?.slug ?? slug) === 'faq' || slug === 'faq';
    const isClasses = resolvedBuiltIn?.slug === 'classes';

    return (
        <article className="game-main-inner game-article-view">
            <nav className="game-breadcrumb" aria-label="Breadcrumb">
                <Link to="/game">{locale === 'pt-BR' ? 'Guia' : 'Guide'}</Link>
                <span>/</span>
                <span>{title}</span>
            </nav>

            <header className="game-article-header">
                {resolvedBuiltIn && (
                    <span className="game-article-topic-icon" aria-hidden>
                        <i className={resolvedBuiltIn.icon} />
                    </span>
                )}
                <h1>{title}</h1>
                {summary && <p className="game-article-summary">{summary}</p>}
            </header>

            <div className="game-article-body">
                {isFaq ? (
                    <div className="game-faq-list">
                        {pageContent.faq.map((item, i) => (
                            <details key={i} className="game-faq-item" open={i === 0}>
                                <summary>{item.q}</summary>
                                <p>{item.a}</p>
                            </details>
                        ))}
                    </div>
                ) : (
                    resolvedBuiltIn ? renderGuideContent(body) : <NewsContent content={body} className="game-guide-content" />
                )}
            </div>

            {isClasses && (
                <p className="game-article-action">
                    <Link to="/classes" className="btn btn-primary">
                        {pageContent.classesTeaser.cta} <i className="ri-arrow-right-line" />
                    </Link>
                </p>
            )}

            {resolvedBuiltIn?.href && resolvedBuiltIn.slug !== 'classes' && (
                <p className="game-article-action">
                    <Link to={resolvedBuiltIn.href} className="btn btn-primary">
                        {locale === 'pt-BR' ? 'Abrir página' : 'Open page'}{' '}
                        <i className="ri-external-link-line" />
                    </Link>
                </p>
            )}

            <p className="game-article-back">
                <Link to="/game">
                    <i className="ri-arrow-left-line" /> {backLabel}
                </Link>
            </p>
        </article>
    );
};

export default GameGuideArticle;
